import { useState, useRef, useEffect } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const caseTypes = [
  'Criminal Defense',
  'Personal Injury',
  'Family Law',
  'Civil Litigation',
  'Drug Crimes',
  'Auto Accident',
  'Wrongful Death',
  'Federal Defense',
  'Expungement',
  'Other',
];

export default function LeadForm() {
  const { ref, inView } = useInView();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const msg = data.get('message') as string;
    if (msg && msg.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, v as string));
      const res = await fetch('https://readdy.ai/api/form/d7a3c8qdqn9mib2trec0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-[#111] py-24 md:py-32" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Info */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">Contact Us</span>
            </div>
            <h2 className="font-serif text-white text-4xl md:text-5xl font-bold mb-5 leading-[1.1]">
              Start With a{' '}
              <span className="text-[#C9A84C]">Free Consultation</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-12">
              Tell us about your situation. An attorney will review your case and reach out to discuss your options — no obligation, no pressure, completely confidential.
            </p>

            <div className="space-y-5">
              {[
                {
                  icon: 'ri-building-2-line',
                  label: 'Santa Maria Office',
                  value: '301 E. Cook Street, Suite E\nSanta Maria, CA 93454-5133',
                  href: 'https://maps.google.com/?q=301+E+Cook+St+Suite+E+Santa+Maria+CA+93454',
                },
                {
                  icon: 'ri-building-2-line',
                  label: 'San Luis Obispo Office',
                  value: '2146 Parker Street, Suite A-3\nSan Luis Obispo, CA 93401',
                  href: 'https://maps.google.com/?q=2146+Parker+St+Suite+A3+San+Luis+Obispo+CA+93401',
                },
                {
                  icon: 'ri-phone-line',
                  label: 'Phone',
                  value: '(805) 922-8308',
                  href: 'tel:8059228308',
                },
                {
                  icon: 'ri-time-line',
                  label: 'Consultation',
                  value: 'Free Initial Consultation Available',
                  href: '#',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 py-4 border-b border-white/6">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#C9A84C]/10">
                    <i className={`${item.icon} text-[#C9A84C] text-base`}></i>
                  </div>
                  <div>
                    <div className="text-white/30 text-[10px] tracking-[0.28em] uppercase mb-1">{item.label}</div>
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-white/75 text-sm leading-relaxed whitespace-pre-line hover:text-[#C9A84C] transition-colors cursor-pointer"
                    >
                      {item.value}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Martindale badges */}
            <div className="flex items-center gap-6 mt-10 pt-8 border-t border-white/6">
              <img
                src="https://cdcssl.ibsrv.net/ibimg/smb/300x88_80/webmgr/21/1/4/Peer_BV_2024_300px.png.webp?6ea4c7ea2b36316fd5e0faac5498b3f1"
                alt="BV Distinguished 2024"
                className="h-10 w-auto object-contain opacity-55"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <img
                src="https://cdcssl.ibsrv.net/ibimg/smb/300x88_80/webmgr/21/1/4/Client-reviewed_300px.png.webp?ddbc93a4f4cb3c6c437d22b143175b37"
                alt="Client Reviewed"
                className="h-10 w-auto object-contain opacity-55"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
          </div>

          {/* Right: Form */}
          <div className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/20 p-8 md:p-10">
              {status === 'success' ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#C9A84C]/10 mx-auto mb-6">
                    <i className="ri-check-line text-[#C9A84C] text-3xl"></i>
                  </div>
                  <h3 className="font-serif text-white text-2xl font-bold mb-3">Message Received</h3>
                  <p className="text-white/45 text-sm leading-relaxed max-w-xs mx-auto">
                    An attorney will be in touch shortly to discuss your case. All inquiries are completely confidential.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-8 text-[#C9A84C] text-sm underline cursor-pointer hover:text-[#b8943e] transition-colors"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="consultation-form"
                  className="space-y-5"
                >
                  <div className="mb-6">
                    <h3 className="font-serif text-white text-xl font-bold mb-1">Request a Free Consultation</h3>
                    <p className="text-white/35 text-sm">We respond within 24 hours. All inquiries are confidential.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase mb-2">Full Name *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="John Smith"
                        className="w-full h-12 bg-transparent border border-white/12 px-4 text-white text-sm placeholder-white/20 focus:border-[#C9A84C] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase mb-2">Phone *</label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="(805) 000-0000"
                        className="w-full h-12 bg-transparent border border-white/12 px-4 text-white text-sm placeholder-white/20 focus:border-[#C9A84C] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase mb-2">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full h-12 bg-transparent border border-white/12 px-4 text-white text-sm placeholder-white/20 focus:border-[#C9A84C] focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase mb-2">Case Type</label>
                    <select
                      name="case_type"
                      className="w-full h-12 bg-[#1A1A1A] border border-white/12 px-4 text-white/75 text-sm focus:border-[#C9A84C] focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="">Select case type...</option>
                      {caseTypes.map((ct) => (
                        <option key={ct} value={ct}>{ct}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase mb-2">
                      Brief Description <span className="text-white/30 normal-case tracking-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Briefly describe your legal situation..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-transparent border border-white/12 px-4 py-3 text-white text-sm placeholder-white/20 focus:border-[#C9A84C] focus:outline-none transition-colors resize-none"
                    />
                    {charCount > 500 && (
                      <p className="text-red-400 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm">Something went wrong. Please try again or call us directly at (805) 922-8308.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="w-full h-14 bg-[#C9A84C] text-black font-bold text-[12px] tracking-[0.28em] uppercase hover:bg-[#b8943e] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'sending' ? 'Sending...' : 'Speak With an Attorney Today →'}
                  </button>

                  <p className="text-white/20 text-xs text-center leading-relaxed">
                    Confidential &bull; No obligation &bull; We respond within 24 hours
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
